import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper,
  Box,
  Typography,
  makeStyles,
  Tooltip,
  IconButton,
  Button,
} from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import GetAppIcon from '@material-ui/icons/GetApp';
import { getCurrentAndFutureCompanyQuotasThunk } from '../redux/management.thunk';
import { selectCurrentAndFutureCompanyQuotas } from '../redux/management.slice';
import { countOrACVFormatter } from '../../../util/customFunctions';
import CompanyQuotaEditorModal from './CompanyQuotaEditorModal';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    maxWidth: '1000px',
  },
  header: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },
  subHeader: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  dataGridContainer: {
    height: '60vh',
    width: '100%',
    overflow: 'hidden',
    '& .MuiDataGrid-root': {
      width: '100%',
    },
    '& .MuiDataGrid-window': {
      left: 0,
      right: 0,
      bottom: 0,
      position: 'absolute',
      overflowX: 'hidden',
    },
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '#4472c4 !important',
      color: 'white !important',
    },
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: '#4472c4 !important',
      color: 'white !important',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 'bold !important',
      color: 'white !important',
    },
    '& .MuiDataGrid-iconSeparator': {
      color: 'white !important',
    },
    '& .MuiDataGrid-sortIcon': {
      color: 'white !important',
    },
    '& .MuiDataGrid-menuIcon': {
      color: 'white !important',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      color: 'white !important',
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#e3ecfa !important',
      },
    },
  },
}));

const AddCompanyQuotaView = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const companyQuotas = useSelector(selectCurrentAndFutureCompanyQuotas);
  const currencySymbol = useSelector(state => state.app.user.Currency);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await dispatch(getCurrentAndFutureCompanyQuotasThunk());
      setLoading(false);
    };
    loadData();
  }, [dispatch]);

  const handleRowClick = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const columns = [
    {
      field: 'Fiscal_Quarter',
      headerName: 'Fiscal Quarter',
      flex: 1,
      minWidth: 150,
      sortable: true,
      disableColumnMenu: true,
    },
    {
      field: 'Quota',
      headerName: 'Quota',
      flex: 1,
      minWidth: 150,
      sortable: true,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => countOrACVFormatter(params.value, currencySymbol),
    },
    {
      field: 'NQP_GOAL',
      headerName: 'NQP Goal',
      flex: 1,
      minWidth: 150,
      sortable: true,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => countOrACVFormatter(params.value, currencySymbol),
    },
  ];

  const rows = useMemo(() => {
    if (!companyQuotas) return [];
    return companyQuotas.map((quota) => ({
      id: quota.Fiscal_Quarter,
      Fiscal_Quarter: quota.Fiscal_Quarter,
      Quota: quota.Quota,
      NQP_GOAL: quota.NQP_GOAL,
    }));
  }, [companyQuotas]);

  // CSV Export Function
  const handleExportCSV = () => {
    const headers = columns.map(col => col.headerName).join(',');
    const csvRows = rows.map(row => {
      return columns.map(col => {
        const field = col.field;
        let value = row[field];

        // Apply formatter if exists
        if (col.valueFormatter && value !== undefined && value !== null) {
          value = col.valueFormatter({ value });
        }

        // Escape commas and quotes in CSV
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          value = `"${value.replace(/"/g, '""')}"`;
        }

        return value;
      }).join(',');
    });

    const csvContent = [headers, ...csvRows].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'company_quota_data.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Paper elevation={2} className={classes.container}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" marginBottom={2}>
          <Box>
            <Typography variant="h2" className={classes.header}>
              Add Company Quota
            </Typography>
            <Typography variant="body1" className={classes.subHeader}>
              View existing company quotas or click on any row to add/modify quota assignments.
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setModalOpen(true)}
              style={{
                marginRight: '8px',
              }}
            >
              Add Company Quota
            </Button>
            <Tooltip title="Download as CSV" arrow>
              <IconButton
                onClick={handleExportCSV}
                style={{
                  padding: '8px',
                  margin: '8px',
                }}
              >
                <GetAppIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <div className={classes.dataGridContainer}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            onRowClick={handleRowClick}
            disableSelectionOnClick
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[10, 25, 50, 100]}
            pagination
            disableColumnMenu
          />
        </div>
      </Paper>

      <CompanyQuotaEditorModal
        open={modalOpen}
        onClose={handleModalClose}
        existingQuotas={companyQuotas || []}
      />
    </>
  );
};

export default AddCompanyQuotaView;
